// src/utils/api.js
import { auth } from '../../firebase/init';

// Change from Cloud Run URL to API Gateway URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safemcc-portal-gateway-v2-bvn24355.ew.gateway.dev' //'https://safemcc-portal-gateway-bvn24355.ew.gateway.dev';

// Helper for getting Firebase auth token
const getAuthToken = async () => {
	try {
		if (!auth.currentUser) {
			throw new Error('No authenticated user');
		}
		return await auth.currentUser.getIdToken(true);
	} catch (error) {
		console.error('Failed to get auth token:', error);
		throw error;
	}
};

// Helper for making authenticated API requests.
const authenticatedRequest = async (endpoint, options = {}) => {
	const fullUrl = `${API_BASE_URL}${endpoint}`;
	
	// Modify to avoid logging sensitive data in options (which could contain headers with tokens)
	const safeOptions = { ...options };
	if (safeOptions.headers) {
		// Don't log headers that might contain tokens
		safeOptions.headers = { ...safeOptions.headers };
		if (safeOptions.headers['Authorization']) {
			safeOptions.headers['Authorization'] = '[REDACTED]';
		}
	}
	
	//console.log(`[permissionsApi.js] authenticatedRequest: Calling URL: ${fullUrl}`, safeOptions);
	
	try {
		const token = await getAuthToken();

		const response = await fetch(fullUrl, {
			...options,
			headers: {
				'Authorization': `Bearer ${token}`,
				'Content-Type': 'application/json',
				...(options.headers || {})
			}
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({ error: response.statusText, status: response.status }));
			console.error(`[permissionsApi.js] authenticatedRequest: Error for ${endpoint} - Status ${response.status}`, errorData);
			throw new Error(errorData.error || `API request failed with status ${response.status}`);
		}

		return await response.json();
	} catch (error) {
		console.error(`[permissionsApi.js] API request error (${endpoint}):`, error);
		throw error;
	}
};

// API service with methods for permissions
export const apiService = {
	// Get user permissions
	getPermissions: async () => {
		const endpointPath = '/api/permissions';
		//console.log(`[permissionsApi.js] getPermissions: Attempting to call endpoint: ${endpointPath}`);
		try {
			return await authenticatedRequest(endpointPath);
		} catch (error) {
			console.error(`[permissionsApi.js] Error in getPermissions calling ${endpointPath}:`, error);
			throw error;
		}
	},

	// Update permissions (for super admins)
	updatePermissions: async (permissions) => {
		const endpointPath = '/api/permissions';
		//console.log(`[permissionsApi.js] updatePermissions: Calling endpoint: ${endpointPath}`);
		return await authenticatedRequest(endpointPath, {
			method: 'POST',
			body: JSON.stringify({ routes: permissions })
		});
	},

	// Get super admins
	getSuperAdmins: async () => {
		const endpointPath = '/api/permissions/super-admins';
		//console.log(`[permissionsApi.js] getSuperAdmins: Attempting to call endpoint: ${endpointPath}`);
		const result = await authenticatedRequest(endpointPath);
		return result.superAdmins || [];
	},

	// Add new method for adding a super admin
	addSuperAdmin: async (email) => {
		const endpointPath = '/api/permissions/super-admins';
		//console.log(`[permissionsApi.js] addSuperAdmin: Calling endpoint: ${endpointPath}`);
		return await authenticatedRequest(endpointPath, {
			method: 'POST',
			body: JSON.stringify({ email })
		});
	},

	// Remove super admin
	removeSuperAdmin: async (email) => {
		const endpointPath = `/api/permissions/super-admins/${encodeURIComponent(email)}`;
		//console.log(`[permissionsApi.js] removeSuperAdmin: Calling endpoint: ${endpointPath}`);
		return await authenticatedRequest(endpointPath, {
			method: 'DELETE'
		});
	},

	// Get full permissions data
	getFullPermissionsData: async () => {
		const endpointPath = '/api/permissions/full';
		//console.log(`[permissionsApi.js] getFullPermissionsData: Attempting to call endpoint: ${endpointPath}`);
		try {
			// Real API call for full permissions data.
			return await authenticatedRequest(endpointPath);
		} catch (error) {
			console.error(`[permissionsApi.js] Error in getFullPermissionsData calling ${endpointPath}:`, error);
			throw error;
		}
	}
};
